/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { isUserAllowed } from "../../helpers/user";
import { clearCookie, getCookie, setCookie } from "../../api/apiClient";
import { getUserInfo } from "../../helpers/user";
import { fetchCurrentUser } from "../../pages/finish-login";
import { User } from "../../types/users";
import MFAEnrollment from "./MFAEnrollment";
import MFAVerification from "./MFAVerification";
import { useMFA } from "../../hooks/useMFA";

interface RequireAuthProps {
  children: JSX.Element;
}

const REDIRECT_URL_KEY = "redirectUrl";
const MFA_VERIFIED_KEY = "mfa_ok";

export const checkAuthentication = (): boolean => {
  const user = getUserInfo();
  const sessionExpiry = getCookie("session_expiry");

  if (!user || !sessionExpiry) {
    return false;
  }

  try {
    const expiryDate = new Date(sessionExpiry);
    if (expiryDate <= new Date()) {
      clearCookie("user_data");
      clearCookie("mfa_ok");
      clearCookie("session_expiry");
      clearCookie("csrf_token");
      return false;
    }
  } catch (error) {
    return false;
  }

  return true;
};

const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showMFAEnrollment, setShowMFAEnrollment] = useState(false);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [
    enrollmentRequestedFromVerification,
    setEnrollmentRequestedFromVerification,
  ] = useState(false);

  const { hasMFAMethods } = useMFA();

  const isAuthenticated = checkAuthentication();

  useEffect(() => {
    const loadUserAndCheckMFA = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        let userData = getUserInfo();

        if (!userData.id) {
          userData = await fetchCurrentUser();
        }

        if (userData) {
          setCurrentUser(userData);

          if (!userData.must_use_mfa) {
            setShowMFAEnrollment(false);
            setShowMFAVerification(false);
            return;
          }

          if (userData.must_use_mfa && !userData.has_mfa_added) {
            setShowMFAEnrollment(true);
          } else if (userData.has_mfa_added && !getCookie(MFA_VERIFIED_KEY)) {
            setShowMFAVerification(true);
          }
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    loadUserAndCheckMFA();
  }, [isAuthenticated]);

  const checkMFAMethodsAvailable = useCallback(async () => {
    if (!currentUser) return;

    try {
      const hasMethods = await hasMFAMethods();

      if (!hasMethods && currentUser.must_use_mfa) {
        setShowMFAEnrollment(true);
        setShowMFAVerification(false);
      } else if (hasMethods && currentUser.must_use_mfa && !getCookie(MFA_VERIFIED_KEY)) {
        setShowMFAEnrollment(false);
        setShowMFAVerification(true);
      } else if (hasMethods && currentUser.must_use_mfa && getCookie(MFA_VERIFIED_KEY)) {
        setShowMFAEnrollment(false);
        setShowMFAVerification(false);
      }
      else if (!currentUser.must_use_mfa) {
        setShowMFAEnrollment(false);
        setShowMFAVerification(false);
      }
    } catch (error) {}
  }, [currentUser, hasMFAMethods]);

  useEffect(() => {
    checkMFAMethodsAvailable();
  }, [currentUser, checkMFAMethodsAvailable]);

  useEffect(() => {
    if (isAuthenticated && !showMFAEnrollment && !showMFAVerification) {
      const redirectUrl = localStorage.getItem(REDIRECT_URL_KEY);

      if (redirectUrl) {
        localStorage.removeItem(REDIRECT_URL_KEY);
        if (redirectUrl !== "/" && redirectUrl !== "/login") {
          window.location.href = redirectUrl;
        }
      }
    }
  }, [isAuthenticated, showMFAEnrollment, showMFAVerification]);

  const handleMFAEnrollmentComplete = async () => {
    try {
      const updatedUser = await fetchCurrentUser();
      if (updatedUser) {
        setCurrentUser(updatedUser);
        setShowMFAEnrollment(false);

        if (enrollmentRequestedFromVerification) {
          setEnrollmentRequestedFromVerification(false);
          setShowMFAVerification(true);
        }
      }
    } catch (error) {}
  };

  const handleMFAVerificationComplete = () => {
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + 24); // 24 hours
    setCookie(MFA_VERIFIED_KEY, "true");
    setShowMFAVerification(false);
  };

  const handleEnrollmentRequestedFromVerification = () => {
    setShowMFAVerification(false);
    setShowMFAEnrollment(true);
    setEnrollmentRequestedFromVerification(true);
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  if (showMFAEnrollment && currentUser) {
    return (
      <MFAEnrollment
        user={currentUser}
        onEnrollmentComplete={handleMFAEnrollmentComplete}
      />
    );
  }

  if (showMFAVerification && currentUser) {
    return (
      <MFAVerification
        user={currentUser}
        onVerificationComplete={handleMFAVerificationComplete}
        onEnrollmentRequested={handleEnrollmentRequestedFromVerification}
      />
    );
  }

  if (!isAuthenticated) {
    localStorage.setItem(REDIRECT_URL_KEY, location.pathname);
    return <Navigate to="/login" replace />;
  }

  if (!isUserAllowed()) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default RequireAuth;
